

import React, { useState } from 'react';
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Typography, CircularProgress } from "@mui/material";
import Card from "@mui/material/Card";
import EntitiesPopup from "layouts/components/EntitiesPopup";

// -----------------------------------------------------------
// 🚀 HELPER FUNCTIONS (Moved outside component for clarity)
// -----------------------------------------------------------

/**
 * Calculates the total engagement for a single data item (one day).
 */
const getEngagementForItem = (item) => {
  let totalEngagement = 0;

  // Aggregate post engagement for Facebook and Instagram
  if (item.posts && item.posts.length > 0) {
    totalEngagement += item.posts.reduce((acc, post) => {
      return acc + (post.postMetrics?.likes || post.postMetrics?.like || 0) + (post.postMetrics?.comments || 0) +
        (post.postMetrics?.shares || 0) + (post.postMetrics?.love || 0) +
        (post.postMetrics?.wow || 0) + (post.postMetrics?.haha || 0) +
        (post.postMetrics?.sad || 0) + (post.postMetrics?.angry || 0);
    }, 0);
  }

  // Aggregate video engagement for YouTube
  if (item.videos && item.videos.length > 0) {
    totalEngagement += item.videos.reduce((acc, video) => {
      return acc + (video.videoMetrics?.likeCount || 0) + (video.videoMetrics?.commentCount || 0);
    }, 0);
  }
  return totalEngagement;
};

/**
 * Creates an efficient lookup Map of [date, engagementValue] for a profile.
 */
const createEngagementMap = (profile) => {
  const map = new Map();
  if (profile && profile.data) {
    profile.data.forEach(item => {
      map.set(item.date, getEngagementForItem(item));
    });
  }
  return map;
};


// -----------------------------------------------------------
// 🚀 MAIN COMPONENT
// -----------------------------------------------------------

function AudienceEngagementCountChart({
  title,
  datasets,
  previousdatasets,
  futuredatasets = [],
  isPredictionEnabled = false,
  isLoading = false,
  entitlements, // ✅ Added prop
  viewMode, // ✅ NEW
  onViewModeChange // ✅ NEW
}) {
  // ✅ State for the popup (unchanged)
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupDate, setPopupDate] = useState(null);

  const handleBarClick = (date) => {
    setPopupDate(date);
    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setPopupDate(null);
  };

  // ⏳ Loader during fetch (unchanged)
  if (isLoading) {
    return (
      <Card>
        <MDBox p={3} display="flex" justifyContent="center" alignItems="center" height="250px">
          <CircularProgress size={32} thickness={4} color="accent" />
        </MDBox>
      </Card>
    );
  }

  // (No data check is unchanged)
  if (!datasets) {
    return (
      <Card>
        <MDBox p={3} textAlign="center">
          <MDTypography variant="body2" color="text">
            No engagement data available.
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  // ⬇️=========================================
  // ⬇️ FIXED: ROBUST DATA & LABEL ALIGNMENT
  // ⬇️=========================================

  // ✅ Use provided Future Data if prediction is enabled
  let futureData = isPredictionEnabled && futuredatasets ? futuredatasets : [];
  // console.log("Future Dataset in chart : ", futureData);

  // ✅ 1. Create a master list of all unique dates from ALL profiles
  const allActualDates = new Set();
  datasets.forEach(profile => profile.data.forEach(item => allActualDates.add(item.date)));

  const allFutureDates = new Set();
  if (isPredictionEnabled && futureData.length > 0) {
    futureData.forEach(profile => profile.data.forEach(item => allFutureDates.add(item.date)));
  }

  // ✅ 2. Combine, sort, and create the final X-axis labels
  const actualLabels = Array.from(allActualDates).sort((a, b) => new Date(a) - new Date(b));
  // Ensure predicted labels are only those *after* the actual labels
  const predictedLabels = Array.from(allFutureDates)
    .sort((a, b) => new Date(a) - new Date(b))
    .filter(date => !allActualDates.has(date));

  const chartLabels = [...actualLabels, ...predictedLabels];

  // ✅ 3. Align previous datasets
  const updatedPreviousDatasets = isPredictionEnabled
    ? [...previousdatasets, ...datasets] // Append actual dataset
    : previousdatasets;

  // ✅ 4. Create chart data, mapping against the master `chartLabels`
  const chartData = {
    labels: chartLabels,
    datasets: [
      ...datasets.map((profile) => {
        // Create a lookup map for this profile's engagement
        const engagementMap = createEngagementMap(profile);
        return {
          label: profile.name || profile.label,
          // Map data against the MASTER chartLabels list, filling gaps with 0
          data: chartLabels.map(date => engagementMap.get(date) || 0),
          profileId: profile.profileId,
          profilePictureUrl: profile.profilePictureUrl || '',
          platform: profile.platform || "Unknown",
          metricName: "Engagement Count",
        };
      }),
      ...futureData.map((profile) => {
        // Create a lookup map for this profile's future engagement
        const engagementMap = createEngagementMap(profile);
        return {
          label: `${profile.name || profile.label} (Predicted)`,
          // Map data against the MASTER chartLabels list, filling gaps with 0
          data: chartLabels.map(date => engagementMap.get(date) || 0),
          profileId: profile.profileId,
          profilePictureUrl: profile.profilePictureUrl || '',
          platform: profile.platform || "Unknown",
          metricName: "Predicted Engagement Count",
        };
      })
    ],
    previousdatasets: updatedPreviousDatasets.map((profile) => {
      // Sort previous data by date to align roughly by "nth day" with the current chart labels
      const sortedHistory = [...(profile.data || [])].sort((a, b) => new Date(a.date) - new Date(b.date));

      return {
        label: profile.name || profile.label,
        // Map data by INDEX against the chartLabels. 
        // Since chartLabels represents the sorted timeline of the current period,
        // we map the sorted timeline of the previous period 1-to-1 by index.
        data: chartLabels.map((_, i) => {
          const item = sortedHistory[i];
          return item ? getEngagementForItem(item) : 0;
        }),
        profileId: profile.profileId,
        profilePictureUrl: profile.profilePictureUrl || '',
        platform: profile.platform || "Unknown",
        metricName: "Engagement Count",
      };
    }),
  };

  // ⬆️=========================================
  // ⬆️ END OF FIXED LOGIC
  // ⬆️=========================================

  // (Tooltip content is unchanged)
  const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        This chart displays the total engagement received on a profile’s posts and videos over time.
        Engagement includes likes, comments, shares, and reactions, helping measure audience
        interaction with content.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>How to read the chart:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        - Each bar represents the total engagement on a given date. <br />
        - Taller bars indicate higher engagement on that day. <br />
        - If multiple profiles are selected, each profile’s engagement is shown as a separate bar.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Identify which days had the highest audience interaction. <br />
        - Compare engagement levels across different profiles. <br />
        - Spot trends in engagement and correlate them with content strategies or campaigns.
      </Typography>
    </div>
  );

  // (Return block is unchanged)
  return (
    <>
      <VerticalBarChart
        title={title}
        description={tooltipContent}
        height="19.125rem"
        chart={chartData}
        aggregate={true}
        is_derived={true}
        onBarClick={handleBarClick} // ✅ Pass the click handler
        rawData={datasets}
        entitlements={entitlements} // ✅ Pass entitlements
        viewMode={viewMode} // ✅ Pass down CORRECT PROP NAME
        onViewModeChange={onViewModeChange} // ✅ Pass down
      />
      {isPopupOpen && (
        <EntitiesPopup
          isModalOpen={isPopupOpen}
          onClose={handlePopupClose}
          datasets={datasets}
          date={popupDate}
        />
      )}
    </>
  );
}

// 🛡️ PropTypes Validation (unchanged)
AudienceEngagementCountChart.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  datasets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string,
      profileId: PropTypes.string,
      profilePictureUrl: PropTypes.string,
      data: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string,
          posts: PropTypes.arrayOf(
            PropTypes.shape({
              postMetrics: PropTypes.shape({
                likes: PropTypes.number,
                like: PropTypes.number,
                comments: PropTypes.number,
                shares: PropTypes.number,
                love: PropTypes.number,
                wow: PropTypes.number,
                haha: PropTypes.number,
                sad: PropTypes.number,
                angry: PropTypes.number,
              }),
            })
          ),
          videos: PropTypes.arrayOf(
            PropTypes.shape({
              videoMetrics: PropTypes.shape({
                likeCount: PropTypes.number,
                commentCount: PropTypes.number,
              }),
            })
          ),
        })
      ),
    })
  ).isRequired,
  previousdatasets: PropTypes.arrayOf(
    PropTypes.shape({
      // ... (same structure as datasets)
    })
  ).isRequired,
  futuredatasets: PropTypes.array, // Kept as array as in original
  isPredictionEnabled: PropTypes.bool, // Kept as bool
  isLoading: PropTypes.bool, // Kept as bool
  entitlements: PropTypes.object, // ✅ Added propType
  viewMode: PropTypes.string,
  onViewModeChange: PropTypes.func,
};

export default AudienceEngagementCountChart;